import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, PageHeader, ScheduleCalendar, SectionCard, StatCard } from "components/admin";
import { buildSchoolCalendarItems } from "pages/app/workspace/sections/helpers/workspace-calendar.helpers";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import {
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import {
  SchoolCalendarSectionSectionSecondary,
} from "pages/app/workspace/sections/styles/school-calendar-section.inline.styles";
export const SchoolCalendarSection: React.FC<{
  embedded?: boolean;
  portalAccess: any;
}> = ({ embedded = false, portalAccess }) => {
  const { schoolId, selectedSchool } = useScopedSchoolSelection({
    portalAccess,
  });
  const schoolDashboardQuery = useQuery({
    queryKey: ["workspace-school-calendar-dashboard", schoolId],
    queryFn: () => workspaceApi.schoolDashboards.get(schoolId),
    enabled: Boolean(schoolId),
  });
  const upcomingMatches = useMemo(
    () => schoolDashboardQuery.data?.upcoming_matches || [],
    [schoolDashboardQuery.data?.upcoming_matches],
  );
  const rosterTasks = useMemo(
    () => schoolDashboardQuery.data?.roster_tasks || [],
    [schoolDashboardQuery.data?.roster_tasks],
  );
  const schoolCalendarItems = useMemo(
    () => buildSchoolCalendarItems(schoolDashboardQuery.data),
    [schoolDashboardQuery.data],
  );
  return (
    <>
      {embedded ? null : <PageHeader title="School Calendar" />}

      {schoolId ? (
        <>
          <SectionStatsGrid $tight={embedded}>
            <StatCard
              helper="Upcoming fixtures currently visible in the selected school scope."
              label="Fixtures"
              value={upcomingMatches.length}
            />
            <StatCard
              helper="Rosters still needing attention before matchday."
              label="Roster Tasks"
              value={rosterTasks.length}
            />
            <StatCard
              helper="Total schedule items merged into the calendar view."
              label="Calendar Items"
              value={schoolCalendarItems.length}
            />
            <StatCard
              helper="Submitted rosters already counted by the dashboard payload."
              label="Submitted Rosters"
              value={schoolDashboardQuery.data?.overview?.submitted_rosters_count ?? 0}
            />
          </SectionStatsGrid>

          <SchoolCalendarSectionSectionSecondary $tight={embedded}>
            <SectionCard
              subtitle={`Planning view for ${
                selectedSchool?.name ||
                schoolDashboardQuery.data?.school?.name ||
                "the selected school"
              }. Choose any item from the side rail to jump the calendar directly to it.`}
              title="Calendar View"
            >
              <ScheduleCalendar
                emptyDescription="Fixtures and roster deadlines will appear here once the selected school has schedule data."
                emptyTitle="No school schedule"
                isLoading={schoolDashboardQuery.isLoading}
                items={schoolCalendarItems}
              />
            </SectionCard>
          </SchoolCalendarSectionSectionSecondary>
        </>
      ) : (
        <EmptyState
          description="Choose a school to load its fixtures and roster deadlines into the calendar."
          title="No school selected"
        />
      )}
    </>
  );
};
